import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import useShopSettings from '../platform/settings/useShopSettings';
import useOperationalStore, {
  STAGES,
  BLOCKER_LABELS,
  isActiveJob,
  isBlocked,
  getActiveBlocker,
  getBlockerAge,
  getStageAge,
  getStageLabel,
  fmtDur,
} from '../platform/store/useOperationalStore';
import NewROModal from './Modals/NewROModal';
import { DEFAULT_LOCATION_ID } from '../platform/events/operationEvents';
import * as mutations from '../graphql/mutations';
import * as queries from '../graphql/queries';

const client = generateClient();
const ACTIVE_ROAD_STATUSES = new Set([
  'OPEN', 'ASSIGNED', 'TRAVELING', 'ARRIVED', 'DIAGNOSING', 'REPAIRING', 'WAITING_PARTS',
]);

// ─── Sub-components ───────────────────────────────────────────────────────────

function KpiTile({ value, label, warn, accent, subtitle }) {
  return (
    <div style={{
      ...S.kpiTile,
      borderTop: `3px solid ${warn ? (accent || '#F59E0B') : '#E2E8F0'}`,
    }}>
      <div style={{ fontSize: 36, fontWeight: 900, color: warn ? (accent || '#F59E0B') : '#0F172A', lineHeight: 1 }}>
        {value}
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.6px', marginTop: 6 }}>
        {label}
      </div>
      {subtitle && (
        <div style={{ fontSize: 11, color: warn ? '#DC2626' : '#94A3B8', marginTop: 4 }}>{subtitle}</div>
      )}
    </div>
  );
}

function SectionHeader({ label, count, warn, accent }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
      <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.8px', textTransform: 'uppercase', color: '#0F172A' }}>
        {label}
      </span>
      {count != null && (
        <span style={{
          fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
          background: warn ? '#FEF3C7' : '#F1F5F9',
          color: warn ? '#92400E' : '#64748B',
          border: `1px solid ${warn ? '#FDE68A' : '#E2E8F0'}`,
        }}>
          {count}
        </span>
      )}
    </div>
  );
}

function BlockerGroup({ type, jobs, now, expanded, onToggle, onSelectRO, accentColor }) {
  const label = BLOCKER_LABELS[type] || type;
  return (
    <div style={{ borderBottom: '1px solid #F1F5F9' }}>
      <button
        style={S.blockerGroupHeader}
        onClick={onToggle}
        className="today-expandable"
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: accentColor || '#F59E0B', display: 'inline-block',
          }} />
          <span style={{ fontWeight: 700, fontSize: 13, color: '#0F172A' }}>{label}</span>
          <span style={{ fontSize: 12, color: '#94A3B8' }}>{jobs.length} {jobs.length === 1 ? 'job' : 'jobs'}</span>
        </div>
        <span style={{ fontSize: 16, color: '#94A3B8', transform: expanded ? 'rotate(90deg)' : 'none', display: 'inline-block', transition: 'transform 0.15s' }}>›</span>
      </button>

      {expanded && (
        <div style={{ paddingBottom: 8 }}>
          {jobs.map(t => {
            const ageMs = getBlockerAge(t, now);
            const blocker = getActiveBlocker(t);
            return (
              <button
                key={t.id}
                style={S.blockerRow}
                onClick={() => onSelectRO(t.id)}
                className="today-row"
              >
                <span style={{ fontWeight: 700, color: '#0F172A', minWidth: 80 }}>{t.repairOrder}</span>
                <span style={{ color: '#475569', flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t.reason || t.faults || '—'}
                </span>
                <span style={{ color: '#64748B', minWidth: 120, textAlign: 'right', fontSize: 12 }}>{t.companyName}</span>
                <span style={{ color: '#F59E0B', minWidth: 70, textAlign: 'right', fontSize: 12, fontWeight: 700 }}>
                  {ageMs ? fmtDur(ageMs) : '—'}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Subscription banner ──────────────────────────────────────────────────────

function SubscriptionBanner({ navigate }) {
  const subscription = useShopSettings(s => s.settings.subscription);
  if (!subscription || !subscription.status) return null;

  const { status, plan, currentPeriodEnd } = subscription;
  if (status === 'active' || status === 'trialing') return null; // no noise when healthy

  const PLAN_NAMES = { flow: 'Flow', ops: 'Ops', full: 'Full' };
  const planLabel = PLAN_NAMES[plan] || plan || '';

  const bg   = status === 'past_due' ? '#FEF3C7' : '#FEE2E2';
  const text = status === 'past_due' ? '#92400E'  : '#991B1B';
  const msg  = status === 'past_due'
    ? `Payment failed on your ${planLabel} plan. Update billing to maintain access.`
    : `Your ${planLabel} plan has been canceled. Subscribe to restore access.`;

  return (
    <div style={{ background: bg, border: `1px solid ${text}22`, borderRadius: 10, padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
      <span style={{ fontSize: 13, color: text, fontWeight: 600, flex: 1 }}>{msg}</span>
      <button
        style={{ background: text, color: '#fff', border: 'none', borderRadius: 7, padding: '7px 14px', fontSize: 12, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}
        onClick={() => navigate('/billing')}
      >
        Manage Plan →
      </button>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function Homepage() {
  const navigate = useNavigate();
  const branding = useShopSettings(s => s.settings.branding);
  const business = useShopSettings(s => s.settings.business);
  const accent = branding.accentColor || '#F59E0B';

  const trucks = useOperationalStore(s => s.trucks);
  const fetchAll = useOperationalStore(s => s.fetchAll);
  const setSelectedTruckId = useOperationalStore(s => s.setSelectedTruckId);
  const setActiveView = useOperationalStore(s => s.setActiveView);

  const [now, setNow] = useState(() => Date.now());
  const [roadCallCount, setRoadCallCount] = useState(null);
  const [expandedBlockerTypes, setExpandedBlockerTypes] = useState({});
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  // Live clock
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Quiet refresh every 30 seconds
  useEffect(() => {
    const t = setInterval(() => {
      fetchAll({ quiet: true });
      setRefreshTick(n => n + 1);
    }, 30_000);
    return () => clearInterval(t);
  }, [fetchAll]);

  // Road service active count
  useEffect(() => {
    client
      .graphql({ query: queries.listRoadServiceCalls, variables: { limit: 200 } })
      .then(res => {
        const items = res.data?.listRoadServiceCalls?.items ?? [];
        setRoadCallCount(items.filter(c => ACTIVE_ROAD_STATUSES.has(c.status)).length);
      })
      .catch(() => setRoadCallCount(0));
  }, [refreshTick]);

  // ─── Computed data ─────────────────────────────────────────────────────────
  const activeTrucks = trucks.filter(isActiveJob);
  const blockedTrucks = activeTrucks.filter(isBlocked);

  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);
  const todayEnd = new Date(now);
  todayEnd.setHours(23, 59, 59, 999);
  const todayStartMs = todayStart.getTime();
  const todayEndMs = todayEnd.getTime();

  const promisesDueToday = activeTrucks.filter(t => {
    if (!t.promisedTime) return false;
    const ms = t.promisedTime * 1000;
    return ms >= todayStartMs && ms <= todayEndMs;
  });
  const pastDuePromises = activeTrucks.filter(t => t.promisedTime && t.promisedTime * 1000 < todayStartMs);
  const promisesHaveOverdue = promisesDueToday.some(t => t.promisedTime * 1000 < now) || pastDuePromises.length > 0;
  const allPromises = [...pastDuePromises, ...promisesDueToday]
    .filter((t, i, arr) => arr.findIndex(x => x.id === t.id) === i)
    .sort((a, b) => a.promisedTime - b.promisedTime);

  const stageCounts = STAGES.map(s => ({
    ...s,
    count: activeTrucks.filter(t => s.statuses.includes(t.status)).length,
  }));

  const agingJobs = activeTrucks
    .map(t => ({ truck: t, age: getStageAge(t, now) }))
    .filter(({ age }) => age.level !== 'fresh')
    .sort((a, b) => b.age.stageMs - a.age.stageMs)
    .slice(0, 5);

  const blockerGroups = {};
  blockedTrucks.forEach(t => {
    const type = getActiveBlocker(t)?.type || 'other';
    if (!blockerGroups[type]) blockerGroups[type] = [];
    blockerGroups[type].push(t);
  });

  // ─── Handlers ──────────────────────────────────────────────────────────────
  const openTruck = (id) => {
    setSelectedTruckId(id);
    navigate('/flow');
  };

  const goToFlow = (view = 'ro-list') => {
    setActiveView(view);
    navigate('/flow');
  };

  const handleAddTruck = async (truckData) => {
    const ts = Math.floor(Date.now() / 1000);

    const activities = [];
    if (truckData.initialNote?.trim()) {
      activities.push({
        id: `act-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        type: 'note',
        text: truckData.initialNote.trim(),
        author: null,
        timestamp: ts,
        photos: [],
        customerFacing: false,
        category: null,
        stage: 'intake',
      });
    }

    const techNumbers = truckData.techNumber ? [String(truckData.techNumber)] : [];

    try {
      const result = await client.graphql({
        query: mutations.createTruck,
        variables: {
          input: {
            repairOrder: truckData.repairOrder,
            locationId: DEFAULT_LOCATION_ID,
            companyName: truckData.companyName,
            truckNumber: truckData.truckNumber || '',
            reason: truckData.reason || null,
            contactPerson: truckData.contactPerson || '',
            phone: truckData.phone || '',
            email: truckData.email || '',
            partsHandling: truckData.partsHandling || '',
            customerCheckInId: truckData.customerCheckInId || null,
            year: truckData.year || '',
            make: truckData.make || '',
            model: truckData.model || '',
            vin: truckData.vin || '',
            mileage: truckData.mileage || '',
            hours: truckData.hours || '',
            faults: truckData.faults || '',
            customerType: truckData.customerType || null,
            poNumber: truckData.poNumber || null,
            status: 'yard',
            position: 0,
            warranty: truckData.warranty || false,
            promisedTime: truckData.promisedTime || null,
            techNumbers: techNumbers.length ? techNumbers : null,
            activities: activities.length ? activities : null,
            startTime: ts,
            lastStatusChange: ts,
            lastNote: ts,
            timeInWip: 0,
            wipStartTime: null,
            isPaused: false,
            notes: [],
            statusHistory: [{ status: 'yard', timeEntered: ts }],
            estimatedTime: 0,
            expired: false,
            warning: false,
            critical: false,
          },
        },
      });
      const newTruckId = result?.data?.createTruck?.id;
      setIsAddModalOpen(false);
      await fetchAll({ quiet: true });
      if (newTruckId) {
        setSelectedTruckId(newTruckId);
        navigate('/flow');
      }
    } catch (err) {
      console.error('[Today] Error creating truck:', err);
    }
  };

  // ─── Formatting ────────────────────────────────────────────────────────────
  const dateStr = new Date(now).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });
  const timeStr = new Date(now).toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', hour12: true,
  });

  const levelColor = (level) => ({
    red: '#DC2626', orange: '#EA580C', yellow: '#D97706',
  }[level] || '#94A3B8');

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>

      {/* ── HEADER ────────────────────────────────────────────────────────── */}
      {/* Logo + Sign Out live in the platform shell now (sidebar / user menu) */}
      <header style={{ ...S.header, borderBottom: `2px solid ${accent}` }}>
        <div style={S.headerCenter} className="today-header-center">
          <div style={{ fontSize: 13, fontWeight: 500, color: '#64748B' }}>{dateStr}</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.5px' }}>{timeStr}</div>
        </div>

        <div style={S.headerRight}>
          <button
            style={{ ...S.ctaBtn, background: accent }}
            onClick={() => goToFlow('flow')}
          >
            Enter Operations →
          </button>
        </div>
      </header>

      {/* ── MAIN ──────────────────────────────────────────────────────────── */}
      <main style={S.main}>

        {/* ── SHOP PULSE ──────────────────────────────────────────────────── */}
        <div style={S.pulseRow} className="today-pulse-row">
          <KpiTile
            value={activeTrucks.length}
            label="Active ROs"
          />
          <KpiTile
            value={blockedTrucks.length}
            label="Blocked"
            warn={blockedTrucks.length > 0}
            accent={accent}
            subtitle={blockedTrucks.length > 0 ? `${Object.keys(blockerGroups).length} type${Object.keys(blockerGroups).length !== 1 ? 's' : ''}` : null}
          />
          <KpiTile
            value={allPromises.length}
            label="Promises Due Today"
            warn={promisesHaveOverdue}
            accent="#DC2626"
            subtitle={pastDuePromises.length > 0 ? `${pastDuePromises.length} past due` : null}
          />
          <KpiTile
            value={roadCallCount === null ? '…' : roadCallCount}
            label="Road Calls Active"
            warn={roadCallCount > 0}
            accent="#7C3AED"
          />
        </div>

        {/* ── BLOCKED JOBS ────────────────────────────────────────────────── */}
        {blockedTrucks.length > 0 && (
          <section style={S.section}>
            <SectionHeader label="Blocked Jobs" count={blockedTrucks.length} warn accent={accent} />
            <div style={S.card}>
              {Object.entries(blockerGroups).map(([type, jobs]) => (
                <BlockerGroup
                  key={type}
                  type={type}
                  jobs={jobs}
                  now={now}
                  expanded={!!expandedBlockerTypes[type]}
                  onToggle={() => setExpandedBlockerTypes(s => ({ ...s, [type]: !s[type] }))}
                  onSelectRO={openTruck}
                  accentColor={accent}
                />
              ))}
            </div>
          </section>
        )}

        {/* ── PIPELINE SNAPSHOT ───────────────────────────────────────────── */}
        <section style={S.section}>
          <SectionHeader label="Pipeline Snapshot" />
          <div style={S.card}>
            <div style={S.stageRow} className="today-stage-row">
              {stageCounts.map((s, i) => (
                <React.Fragment key={s.id}>
                  <button
                    style={S.stageTile}
                    onClick={() => goToFlow('ro-list')}
                    className="today-stage-tile"
                    title={`${s.count} job${s.count !== 1 ? 's' : ''} in ${s.label}`}
                  >
                    <div style={{ fontSize: 32, fontWeight: 900, color: s.count > 0 ? '#0F172A' : '#CBD5E1', lineHeight: 1 }}>
                      {s.count}
                    </div>
                    <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.6px', color: '#64748B', textTransform: 'uppercase', marginTop: 6 }}>
                      {s.label}
                    </div>
                  </button>
                  {i < stageCounts.length - 1 && (
                    <div style={{ color: '#CBD5E1', fontSize: 20, alignSelf: 'center', userSelect: 'none' }}>›</div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>

        {/* ── AGING JOBS + PROMISES ────────────────────────────────────────── */}
        <div style={S.twoCol} className="today-two-col">

          <section style={S.section}>
            <SectionHeader label="Aging Jobs" count={agingJobs.length} warn={agingJobs.some(j => j.age.level === 'red')} />
            <div style={S.card}>
              {agingJobs.length === 0 ? (
                <div style={S.empty}>No aging jobs right now</div>
              ) : (
                <table style={S.table}>
                  <thead>
                    <tr>
                      {['RO', 'Company', 'Stage', 'In Stage', 'Tech'].map(h => (
                        <th key={h} style={S.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {agingJobs.map(({ truck: t, age }) => (
                      <tr key={t.id} style={S.tr} onClick={() => openTruck(t.id)} className="today-row">
                        <td style={{ ...S.td, fontWeight: 700 }}>{t.repairOrder}</td>
                        <td style={{ ...S.td, ...S.truncate }}>{t.companyName}</td>
                        <td style={S.td}>{getStageLabel(t.status)}</td>
                        <td style={{ ...S.td, fontWeight: 700, color: levelColor(age.level) }}>
                          {fmtDur(age.stageMs)}
                        </td>
                        <td style={{ ...S.td, color: '#64748B' }}>
                          {(t.techNumbers || []).join(', ') || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          <section style={S.section}>
            <SectionHeader
              label="Promises Due Today"
              count={allPromises.length}
              warn={promisesHaveOverdue}
            />
            <div style={S.card}>
              {allPromises.length === 0 ? (
                <div style={S.empty}>No promises set for today</div>
              ) : (
                <table style={S.table}>
                  <thead>
                    <tr>
                      {['RO', 'Company', 'Promised', 'Stage'].map(h => (
                        <th key={h} style={S.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {allPromises.map(t => {
                      const ms = t.promisedTime * 1000;
                      const overdue = ms < now;
                      return (
                        <tr
                          key={t.id}
                          style={{ ...S.tr, background: overdue ? '#FEF2F2' : undefined }}
                          onClick={() => openTruck(t.id)}
                          className="today-row"
                        >
                          <td style={{ ...S.td, fontWeight: 700, color: overdue ? '#DC2626' : '#0F172A' }}>
                            {t.repairOrder}
                          </td>
                          <td style={{ ...S.td, ...S.truncate }}>{t.companyName}</td>
                          <td style={{ ...S.td, color: overdue ? '#DC2626' : '#0F172A', fontWeight: overdue ? 700 : 400 }}>
                            {new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                            {overdue && ' ⚠'}
                          </td>
                          <td style={{ ...S.td, color: '#64748B' }}>{getStageLabel(t.status)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        </div>

        {/* ── SUBSCRIPTION ALERT ──────────────────────────────────────────── */}
        <SubscriptionBanner navigate={navigate} />

        {/* ── QUICK ACTIONS ───────────────────────────────────────────────── */}
        <section style={{ ...S.section, marginTop: 4 }}>
          <div style={S.actionRow}>
            <button
              style={{ ...S.actionBtn, background: accent, color: '#fff', fontWeight: 800, border: 'none' }}
              onClick={() => setIsAddModalOpen(true)}
            >
              + New RO
            </button>
            <button style={S.actionBtn} onClick={() => goToFlow('ro-list')}>
              Flow Board
            </button>
            <button style={S.actionBtn} onClick={() => goToFlow('command')}>
              Command
            </button>
            <button style={S.actionBtn} onClick={() => goToFlow('road-service')}>
              Dispatch
            </button>
            <button style={S.actionBtn} onClick={() => navigate('/analytics')}>
              Intelligence
            </button>
            <button style={{ ...S.actionBtn, borderColor: '#6366f1', color: '#6366f1' }} onClick={() => navigate('/billing')}>
              Plan & Billing
            </button>
          </div>
        </section>

      </main>

      {isAddModalOpen && (
        <NewROModal
          onClose={() => setIsAddModalOpen(false)}
          onSubmit={handleAddTruck}
          customerCheckIns={[]}
          onCustomerProcessed={() => {}}
        />
      )}

      <style>{`
        .today-row { cursor: pointer; transition: background 0.1s; }
        .today-row:hover td { background: #F8FAFC !important; }
        .today-expandable { transition: background 0.1s; }
        .today-expandable:hover { background: #F8FAFC !important; }
        .today-stage-tile { transition: background 0.12s, transform 0.12s; }
        .today-stage-tile:hover { background: #F1F5F9 !important; transform: translateY(-2px); }
        @media (max-width: 900px) {
          .today-two-col { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 680px) {
          .today-pulse-row { grid-template-columns: 1fr 1fr !important; }
          .today-header-center { display: none !important; }
          .today-stage-row { flex-wrap: wrap; gap: 8px !important; }
        }
      `}</style>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const S = {
  page: {
    minHeight: '100vh',
    background: '#F1F5F9',
    color: '#0F172A',
    fontFamily: "'Inter','Manrope','Outfit','Segoe UI',sans-serif",
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    height: 52,
    padding: '0 20px',
    background: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'sticky',
    top: 0,
    zIndex: 50,
    boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
    gap: 16,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 0,
    flex: '0 0 auto',
  },
  logo: {
    height: 28,
    width: 'auto',
    display: 'block',
  },
  headerCenter: {
    flex: 1,
    textAlign: 'center',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flex: '0 0 auto',
  },
  ctaBtn: {
    padding: '7px 16px',
    borderRadius: 0,
    border: 'none',
    color: '#0F172A',
    fontSize: 13,
    fontWeight: 800,
    cursor: 'pointer',
    letterSpacing: '0.2px',
    whiteSpace: 'nowrap',
  },
  signOutBtn: {
    padding: '6px 12px',
    borderRadius: 0,
    border: '1px solid #E2E8F0',
    background: '#FFFFFF',
    color: '#64748B',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  main: {
    width: '100%',
    padding: '16px 20px 32px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  pulseRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 8,
  },
  kpiTile: {
    background: '#FFFFFF',
    borderRadius: 0,
    padding: '14px 16px',
    boxShadow: 'none',
    border: '1px solid #E2E8F0',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
  },
  card: {
    background: '#FFFFFF',
    borderRadius: 0,
    border: '1px solid #E2E8F0',
    boxShadow: 'none',
    overflow: 'hidden',
  },
  stageRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
    padding: '4px 0',
    gap: 0,
  },
  stageTile: {
    flex: 1,
    background: 'transparent',
    border: 'none',
    borderRadius: 0,
    padding: '12px 8px',
    cursor: 'pointer',
    textAlign: 'center',
  },
  twoCol: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 14,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    padding: '10px 14px',
    textAlign: 'left',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    color: '#94A3B8',
    borderBottom: '1px solid #F1F5F9',
    background: '#FAFAFA',
  },
  tr: {
    borderBottom: '1px solid #F8FAFC',
  },
  td: {
    padding: '10px 14px',
    fontSize: 13,
    color: '#334155',
  },
  truncate: {
    maxWidth: 130,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  empty: {
    padding: '28px 20px',
    color: '#94A3B8',
    fontSize: 13,
    textAlign: 'center',
  },
  blockerGroupHeader: {
    width: '100%',
    padding: '12px 16px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    textAlign: 'left',
  },
  blockerRow: {
    width: '100%',
    padding: '9px 20px',
    background: 'transparent',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    cursor: 'pointer',
    fontSize: 13,
    textAlign: 'left',
  },
  actionRow: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
  actionBtn: {
    padding: '10px 20px',
    borderRadius: 8,
    border: '1px solid #E2E8F0',
    background: '#FFFFFF',
    color: '#334155',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
  },
};
